
import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Globe, CheckCircle, AlertCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { dataService } from '../services/dataService';

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
}

export const Contact: React.FC = () => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm<ContactFormData>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    try {
      // 1. Send to DB & Admin notification
      await dataService.submitContactInquiry(data);
      
      // 2. Immediate cleanup
      reset(); 
      setIsSuccess(true);
      
      // 3. Auto-revert after 8 seconds or stay for user review
      setTimeout(() => setIsSuccess(false), 8000);
    } catch (err) {
      console.error(err);
      alert("Submission failed. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-dark min-h-screen py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-4xl font-bold text-white text-center mb-12 uppercase tracking-tight font-heading">Connect With <span className="text-primary">Dakar</span></h1>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Info */}
          <div className="bg-card rounded-xl p-8 border border-gray-800">
            <h2 className="text-2xl font-bold text-white mb-8 uppercase font-heading tracking-wide border-b border-gray-700 pb-4">Our Locations</h2>
            
            <div className="space-y-10">
              {/* Chicago HQ */}
              <div className="flex items-start gap-5">
                <div className="bg-primary/10 p-3 rounded-xl">
                  <MapPin className="h-6 w-6 text-primary flex-shrink-0" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white uppercase tracking-widest mb-2 flex items-center gap-2">
                    Chicago Headquarters
                  </h3>
                  <p className="text-gray-400">6115 S Ashland Ave</p>
                  <p className="text-gray-400">Chicago, IL 60636, USA</p>
                  <div className="mt-3 text-xs text-primary font-bold uppercase tracking-widest flex items-center gap-2">
                    <Mail size={12} /> info@dakarautosales.com
                  </div>
                </div>
              </div>

              {/* Senegal Branch */}
              <div className="flex items-start gap-5">
                <div className="bg-accent/10 p-3 rounded-xl">
                  <Globe className="h-6 w-6 text-accent flex-shrink-0" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white uppercase tracking-widest mb-2">
                    Dakar Branch
                  </h3>
                  <p className="text-gray-400">334 Rue MZ 26</p>
                  <p className="text-gray-400">Dakar, Senegal</p>
                  <div className="mt-3 text-xs text-accent font-bold uppercase tracking-widest flex items-center gap-2">
                    <Mail size={12} /> info.senegal@dakarautosales.com
                  </div>
                </div>
              </div>

              <div className="pt-8 border-t border-gray-800 grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div className="flex items-start gap-4">
                  <Phone className="h-6 w-6 text-danger flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-1">Call Us</h3>
                    <p className="text-gray-400 text-lg font-bold">+1 773-863-9999</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <Mail className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-1">USA Support</h3>
                    <p className="text-gray-400 text-sm">info@dakarautosales.com</p>
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-5 pt-4">
                <div className="bg-gray-800/50 p-3 rounded-xl">
                  <Clock className="h-6 w-6 text-gray-400 flex-shrink-0" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-white uppercase tracking-widest mb-2">Operation Hours (CST)</h3>
                  <div className="text-gray-400 text-sm grid grid-cols-2 gap-x-8 gap-y-1">
                    <span>Mon - Fri:</span> <span>9:00 AM - 7:00 PM</span>
                    <span>Saturday:</span> <span>10:00 AM - 6:00 PM</span>
                    <span>Sunday:</span> <span className="text-danger font-bold">CLOSED</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-card rounded-xl p-8 border border-gray-800 relative overflow-hidden">
            <h2 className="text-2xl font-bold text-white mb-6 uppercase font-heading tracking-wide border-b border-gray-700 pb-4">Send an Inquiry</h2>
            
            {isSuccess ? (
              <div className="flex flex-col items-center justify-center py-20 animate-fade-in text-center">
                <div className="bg-primary/20 p-6 rounded-full mb-6">
                  <CheckCircle size={64} className="text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2 uppercase tracking-tight">Message Dispatched</h3>
                <p className="text-gray-400 mb-8">Your message has been received. Notification sent to <span className="text-white">info@dakarautosales.com</span>.</p>
                <button 
                  onClick={() => setIsSuccess(false)}
                  className="text-xs font-bold uppercase tracking-widest text-primary border-b border-primary pb-1"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2">Full Name</label>
                    <input 
                      {...register('name', { required: true })}
                      type="text" 
                      className={`w-full bg-darker border rounded-lg px-4 py-3 text-white focus:ring-1 focus:ring-primary outline-none transition-all ${errors.name ? 'border-danger' : 'border-gray-700'}`} 
                      placeholder="Enter name" 
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2">Email Address</label>
                    <input 
                      {...register('email', { required: true, pattern: /^\S+@\S+$/i })}
                      type="email" 
                      className={`w-full bg-darker border rounded-lg px-4 py-3 text-white focus:ring-1 focus:ring-primary outline-none transition-all ${errors.email ? 'border-danger' : 'border-gray-700'}`} 
                      placeholder="email@example.com" 
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2">Subject</label>
                  <select 
                    {...register('subject', { required: true })}
                    className="w-full bg-darker border border-gray-700 rounded-lg px-4 py-3 text-white focus:ring-1 focus:ring-primary outline-none transition-all cursor-pointer"
                  >
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Financing Options">Financing Options</option>
                    <option value="International Export">International Export</option>
                    <option value="Schedule a Viewing">Schedule a Viewing</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500 mb-2">Detailed Message</label>
                  <textarea 
                    {...register('message', { required: true })}
                    rows={5} 
                    className={`w-full bg-darker border rounded-lg px-4 py-3 text-white focus:ring-1 focus:ring-primary outline-none transition-all resize-none ${errors.message ? 'border-danger' : 'border-gray-700'}`} 
                    placeholder="How can we help you today?"
                  ></textarea>
                </div>
                <button 
                  disabled={isSubmitting}
                  type="submit"
                  className="w-full bg-primary hover:bg-white hover:text-primary text-white font-heading font-bold py-4 rounded-xl transition-all shadow-xl shadow-primary/10 uppercase tracking-[0.2em] disabled:opacity-50"
                >
                  {isSubmitting ? 'Dispatching...' : 'Dispatch Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
